import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import Slider from 'rc-slider';
import { Component } from 'react';
import { _ } from '/@/locale';
import styles from './styles.module.css';

const colors = {
    red: '#D44D21',
    gray: '#F1F1F1',
    yellow: '#FFD966',
    orange: '#FFC001',
    darkGray: '#A6A6A6',
    darkGreen: '#279E41',
    lightGreen: '#C7EC91',
};

const precision = 0;

@autobind
export default class EnvironmentSlider extends Component {
    static propTypes = {
        dataTestId: PropTypes.string,
        min: PropTypes.number,
        max: PropTypes.number,
        plantingDate: PropTypes.string,
        percentiles: PropTypes.arrayOf(PropTypes.number),
        environment: PropTypes.number,
        children: PropTypes.node,
        step: PropTypes.number,
        crop: PropTypes.oneOf(['corn', 'soy']),
        onEnvironmentChange: PropTypes.func, // TODO v3: remove
        onAfterChange: PropTypes.func,
    };

    static defaultProps = {
        min: 0,
        max: 0,
        percentiles: [],
        step: 10,
    };

    static contextTypes = {
        units: PropTypes.object,
    };

    state = {
        color: undefined,
        currentValue: this.props.environment,
    };

    componentDidMount() {
        this.calculate(this.props.environment, this.props.percentiles);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.environment !== this.props.environment) {
            this.setState({ currentValue: nextProps.environment });
        }
    }

    componentDidUpdate(prevProps) {
        const { plantingDate, environment, percentiles, min, max } = this.props;

        const useOptimalEnvironment = prevProps.percentiles.join() !== percentiles.join();
        const shouldRecalculate =
            prevProps.percentiles.join() !== percentiles.join() ||
            prevProps.min !== min ||
            prevProps.max !== max ||
            prevProps.environment !== environment ||
            prevProps.plantingDate !== plantingDate;

        if (shouldRecalculate) this.calculate(environment, percentiles, useOptimalEnvironment);
        if (prevProps.environment !== environment) this.setColor(environment);
    }

    getInnerMin() {
        return this.props.percentiles.length ? Math.max(this.props.min, this.props.percentiles[0]) : this.props.min;
    }

    getInnerMax() {
        return this.props.percentiles.length
            ? Math.min(this.props.max, this.props.percentiles[this.props.percentiles.length - 1])
            : this.props.max;
    }

    getOuterMin() {
        return this.props.min;
    }

    getOuterMax() {
        return this.props.max;
    }

    getRangeOffset(value) {
        const outerMin = this.getOuterMin();
        const outerMax = this.getOuterMax();
        const offset = (value - outerMin) / (outerMax - outerMin);
        return Math.max(0, Math.min(1, offset));
    }

    getValueInCustomUnit(value) {
        return this.context.units.toYield(value, this.props.crop, precision);
    }

    getValueInBaseUnit(value) {
        const customUnitValue = this.getValueInCustomUnit(value);
        return this.context.units.fromYield(customUnitValue.value, this.props.crop, precision);
    }

    calculate(environment, percentiles, useOptimalEnvironment) {
        const gradientStops = this.calculateGradientStops(percentiles);
        this.drawGradient(gradientStops);

        if (!environment || useOptimalEnvironment) {
            environment = percentiles.length
                ? percentiles[Math.floor(percentiles.length / 2)]
                : this.props.min + (this.props.max - this.props.min) / 2;
        }

        this.setColor(environment);

        if (this.props.onEnvironmentChange) {
            this.props.onEnvironmentChange({ value: this.getValueInBaseUnit(environment) });
        }
    }

    calculateGradientStops(percentiles) {
        const colorOptions = {
            1: ['darkGreen'],
            3: ['yellow', 'darkGreen', 'yellow'],
            5: ['yellow', 'lightGreen', 'darkGreen', 'lightGreen', 'yellow'],
            7: ['orange', 'yellow', 'lightGreen', 'darkGreen', 'lightGreen', 'yellow', 'orange'],
        };

        const colorSequence = colorOptions[percentiles.length];

        if (!colorSequence)
            return [
                [0, colors.gray],
                [1, colors.gray],
            ];

        let gradientStops = percentiles.map((percentile, colorIndex) => [
            this.getRangeOffset(percentile),
            colors[colorSequence[colorIndex]],
        ]);

        gradientStops = [
            [0, colors.gray],
            [Math.max(0, gradientStops[0][0] - 0.01), colors.gray],
            ...gradientStops,
            [Math.min(1, gradientStops[gradientStops.length - 1][0] + 0.01), colors.gray],
            [1, colors.gray],
        ];

        return gradientStops;
    }

    drawGradient(gradientStops) {
        this.canvas.width = this.canvas.offsetWidth;
        this.canvas.height = this.canvas.offsetHeight;

        const gradient = this.canvasContext.createLinearGradient(0, 0, this.canvas.width, this.canvas.height);
        gradientStops.forEach(([offset, color]) => gradient.addColorStop(offset, color));

        this.canvasContext.fillStyle = gradient;
        this.canvasContext.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }

    setColor(value) {
        let color;
        if (this.props.percentiles.length && this.getInnerMin() <= value && value <= this.getInnerMax()) {
            color = this.canvasContext.getImageData(
                Math.floor((this.canvas.width - 1) * this.getRangeOffset(value)),
                Math.floor(this.canvas.height / 2),
                1,
                1
            );
            color = `rgba(${color.data.join(',')})`;
        } else {
            color = colors.darkGray;
        }

        this.setState({ color });
    }

    setCanvasRef(ref) {
        this.canvas = ref;
        this.canvasContext = this.canvas ? this.canvas.getContext('2d') : null;
    }

    getMarks() {
        const innerMin = this.getInnerMin();
        const innerMax = this.getInnerMax();
        const outerMin = this.getOuterMin();
        const outerMax = this.getOuterMax();

        const marks = {
            [outerMin]: <div className={styles.markDown}>{this.getValueInCustomUnit(outerMin).localeValue}</div>,
            [outerMax]: <div className={styles.markDown}>{this.getValueInCustomUnit(outerMax).localeValue}</div>,
        };

        if (innerMin !== outerMin) {
            marks[innerMin] = <div className={styles.markUp}>{this.getValueInCustomUnit(innerMin).localeValue}</div>;
        }
        if (innerMax !== outerMax) {
            marks[innerMax] = <div className={styles.markUp}>{this.getValueInCustomUnit(innerMax).localeValue}</div>;
        }

        return marks;
    }

    handleEnvironmentChange(value) {
        this.setState({ currentValue: this.getValueInBaseUnit(value) });

        if (this.props.onEnvironmentChange) {
            this.props.onEnvironmentChange({ value: this.getValueInBaseUnit(value) });
        }
    }

    handleEnvironmentAfterChange(value) {
        if (this.props.onEnvironmentChange) {
            this.props.onEnvironmentChange({ value: this.getValueInBaseUnit(value), track: true });
        }

        if (this.props.onAfterChange) {
            this.props.onAfterChange(this.getValueInBaseUnit(value));
        }
    }

    renderReferences() {
        const references = [
            { label: _('Muy alta'), color: colors.darkGreen },
            { label: _('Alta'), color: colors.lightGreen },
            { label: _('Media'), color: colors.orange },
            { label: _('Muy baja'), color: colors.darkGray },
        ];
        return (
            <div>
                <div className={styles.referencesTitle}>{_('Referencias')}</div>
                <div className={styles.referencesContainer}>
                    <p>{_('Probabilidad de ocurrencia')}:</p>
                    <div className={styles.references}>
                        {references.map((reference) => (
                            <div key={reference.label} className={styles.reference}>
                                <div className={styles.circle} style={{ background: reference.color }} />
                                <div className={styles.text}>{reference.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    renderValue() {
        if (!this.props.environment) return null;

        const environment = this.getValueInCustomUnit(this.state.currentValue);
        return (
            <div className={styles.value}>
                <div className={styles.number} style={{ color: this.state.color }}>
                    {environment.localeValue}
                    <div className={styles.unit}>{environment.unit}</div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className={classNames(styles.root, this.props.disabled && styles.disabled)}>
                {this.props.children}
                {this.renderValue()}
                <div className={styles.sliderContainer}>
                    <div className={styles.slider} data-testid={this.props.dataTestId}>
                        <Slider
                            min={this.getOuterMin()}
                            max={this.getOuterMax()}
                            marks={this.getMarks()}
                            step={this.props.step}
                            value={this.state.currentValue}
                            onChange={this.handleEnvironmentChange}
                            // onAfterChange={this.handleEnvironmentAfterChange}
                            // TODO: check if works
                            onChangeComplete={this.handleEnvironmentAfterChange}
                            disabled={this.props.disabled}
                        />
                        <canvas className={styles.gradient} ref={this.setCanvasRef} />
                    </div>
                </div>
                {this.props.percentiles.length > 0 && this.renderReferences()}
            </div>
        );
    }
}
